import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Injectable
} from '@angular/core';

import {
    MatDialogRef,
    MAT_DIALOG_DATA,
    MatDialog
} from '@angular/material/dialog';

import { SalesItems } from 'tilby-models';
import { mobileCheck } from "src/utilities";

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent
} from '@tilby/tilby-ui-lib/components/tilby-dialog';

import { lastValueFrom } from 'rxjs';
import { BarcodeComponent, BarcodeScannerComponent } from 'src/app/shared/components';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';

interface DataRefundItemsSelectorDialog {
    saleItems: (SalesItems & {
        _quantity?: number,
        _quantities?: number[]
    })[];
}

@Component({
    standalone: true,
    selector: 'app-refund-items-selector-dialog',
    templateUrl: './refund-items-selector-dialog.component.html',
    styleUrls: ['./refund-items-selector-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        BarcodeComponent,
        BarcodeScannerComponent,
        CommonModule,
        FormsModule,
        MatButton,
        MatLabel,
        MatOption,
        MatFormField,
        MatSelect,
        TilbyCurrencyPipe,
        TilbyDialogToolbarComponent,
        TilbyDialogContentComponent,
        TranslateModule,
        ScrollingModule,
    ]
})
export class RefundItemsSelectorDialogComponent {
    private readonly dialogRef = inject(MatDialogRef);
    private readonly data: DataRefundItemsSelectorDialog = inject(MAT_DIALOG_DATA);

    protected saleItems = structuredClone(this.data.saleItems);

    constructor(
    ) {
        for (const saleItem of this.saleItems) {
            saleItem._quantities = Number.isInteger(saleItem.quantity)
                ? Array.from({ length: saleItem.quantity + 1 }, (_, i) => i)
                : [0, saleItem.quantity];

            saleItem._quantity = 0;
        }
    }

    check() {
        const saleItems = this.saleItems.filter(si => si._quantity).map(si => {
            const { _quantity, _quantities, ...saleItem } = si;

            saleItem.quantity = _quantity!;

            return saleItem;
        });

        this.dialogRef.close(structuredClone(saleItems));
    }

    selectAll() {
        for (const saleItem of this.saleItems) {
            const quantities = saleItem._quantities || [];

            if(quantities.length) {
                saleItem._quantity = quantities[quantities.length - 1];
            }
        }
    }

    unselectAll() {
        for (const saleItem of this.saleItems) {
            saleItem._quantity = 0;
        }
    }

    onBarcodeScanned = (barcode: String) => {
        for (const saleItem of this.saleItems) {
            if (saleItem.barcode === barcode) {
                const quantities = saleItem._quantities || [];
                const quantityIndex = quantities.findIndex(qty => qty === saleItem._quantity);

                if(quantityIndex < (quantities.length - 1)) {
                    saleItem._quantity = quantities[quantityIndex + 1];
                    return;
                }
            }
        }
    }

    hasCheckDisabled() {
        //TODO: make this reactive
        return this.saleItems.every((saleItem) => saleItem._quantity! <= 0);
    }

    isMobile = mobileCheck();
}

@Injectable({
    providedIn: 'root'
})
export class RefundItemsSelectorDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog(options: NonNullableConfigData<DataRefundItemsSelectorDialog>): Promise<SalesItems[]> {
        const config: NonNullableConfigData<DataRefundItemsSelectorDialog> = {
            ...this.switchMobileDesktopDimensions({ height: '70vh', width: '800px' }),
            disableClose: true,
            ...options
        };

        return lastValueFrom(this.dialogRef.open(RefundItemsSelectorDialogComponent, config).afterClosed()).then((res: SalesItems[] | undefined) => res || []);
    }
}
